import { z } from 'zod'
import { eventDocumentKindSchema } from '../enums/event-document-kind.enum.js'
import { httpUrlSchema } from '../helpers/asset-ref.js'

/**
 * Create / register a document on an event.
 *
 * Provide either:
 * - `url` — an already-hosted file (R2 public URL, Social Tables CDN, etc.), or
 * - `data` — a `data:<mime>;base64,...` payload the API stores in object storage.
 *
 * `contentType` is required with `url`; with `data` it is taken from the data URL
 * when omitted. `externalKey` supports idempotent imports (`st:<id>`).
 */
/** What both ways of adding a document carry. */
const eventDocumentCommon = z.object({
  name: z.string().trim().min(1, 'Name is required').max(300),
  kind: eventDocumentKindSchema.optional(),
  sizeBytes: z.number().int().nonnegative().nullish(),
  externalKey: z.string().trim().min(1).max(200).nullish(),
})

/**
 * Characters allowed in an upload data URL. The API stores the bytes and serves
 * them with this type, and the browser opens that URL, so the type has to be one
 * that cannot run as a page. SVG, HTML and script are not on it. A link to a file
 * that is already hosted may still say `application/octet-stream` — imports do.
 */
export const EVENT_DOCUMENT_UPLOAD_MIME_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
  'text/plain',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const

export type EventDocumentUploadMime = (typeof EVENT_DOCUMENT_UPLOAD_MIME_TYPES)[number]

const UPLOAD_MIMES: ReadonlySet<string> = new Set(EVENT_DOCUMENT_UPLOAD_MIME_TYPES)

/** `accept` for the file input, the same set as {@link EVENT_DOCUMENT_UPLOAD_MIME_TYPES}. */
export const EVENT_DOCUMENT_UPLOAD_ACCEPT =
  '.pdf,.png,.jpg,.jpeg,.webp,.gif,.txt,.doc,.docx,.xls,.xlsx'

/** The data URL cap. A larger body is refused before it is stored. */
export const EVENT_DOCUMENT_DATA_URL_MAX_CHARS = 20_000_000

/**
 * Largest file that still fits the data URL cap after `data:<mime>;base64,`.
 * Checked before the file is read, so a too-large file never becomes a string.
 */
export const EVENT_DOCUMENT_UPLOAD_MAX_BYTES = Math.floor(
  ((EVENT_DOCUMENT_DATA_URL_MAX_CHARS - 100) * 3) / 4,
)

const DATA_URL_PREFIX =
  /^data:([a-zA-Z0-9][a-zA-Z0-9!#$&\-^_+.]*)\/([a-zA-Z0-9][a-zA-Z0-9!#$&\-^_+.]*);base64,/

/** The mime a data URL names, lowercased, when it has the shape an upload uses. */
export function eventDocumentDataUrlMime(value: string): string | null {
  const match = DATA_URL_PREFIX.exec(value)
  if (match?.[1] === undefined || match[2] === undefined) return null
  return `${match[1]}/${match[2]}`.toLowerCase()
}

export function isEventDocumentUploadMime(value: string): value is EventDocumentUploadMime {
  return UPLOAD_MIMES.has(value)
}

/** Register a file that is already hosted somewhere. Its type must be stated. */
export const registerEventDocumentInputSchema = eventDocumentCommon
  .extend({
    // http(s) only. `z.string().url()` also accepts `javascript:` and `data:`,
    // and a stored link is opened in the browser and re-read as the event.
    url: z.string().trim().pipe(httpUrlSchema),
    contentType: z.string().trim().min(1).max(200),
  })
  .strict()

/** Upload a file the API stores itself. Its type is read from the data URL. */
export const uploadEventDocumentInputSchema = eventDocumentCommon
  .extend({
    /**
     * Base64 data URL for upload (PDF, images, common office types). Cap keeps
     * request bodies reasonable (~15 MB of base64).
     */
    data: z
      .string()
      .max(EVENT_DOCUMENT_DATA_URL_MAX_CHARS, 'File is too large')
      .refine((value) => {
        const mime = eventDocumentDataUrlMime(value)
        return mime !== null && isEventDocumentUploadMime(mime)
      }, 'That file type can’t be uploaded. Use a PDF, image, text file, or Word or Excel document.'),
    contentType: z.string().trim().min(1).max(200).optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.contentType === undefined) return
    const mime = eventDocumentDataUrlMime(value.data)
    if (mime === null || value.contentType.toLowerCase() === mime) return
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['contentType'],
      message: 'File type doesn’t match the upload.',
    })
  })

/**
 * Add a document to an event, one of two ways — and they are genuinely two
 * shapes, not one shape with holes in it.
 *
 * As three optional fields plus a pair of refines (url XOR data; contentType
 * required with url), the inferred type made all three optional and the service
 * had to re-check `if (!url || !contentType) throw` at runtime to get back what
 * the schema already guaranteed. The union says it in the type instead:
 * `'data' in input` narrows to exactly the branch's fields.
 */
export const createEventDocumentInputSchema = z.union(
  [registerEventDocumentInputSchema, uploadEventDocumentInputSchema],
  {
    error:
      'Provide either a url with its contentType, or a base64 data URL to upload (not both).',
  },
)

export type CreateEventDocumentInput = z.infer<typeof createEventDocumentInputSchema>

/** Rename or re-classify an existing document (not replace the file). */
export const updateEventDocumentInputSchema = z
  .object({
    name: z.string().trim().min(1).max(300).optional(),
    kind: eventDocumentKindSchema.optional(),
  })
  .strict()

export type UpdateEventDocumentInput = z.infer<typeof updateEventDocumentInputSchema>

/**
 * The sentence to show when a document was refused.
 *
 * A url and an upload are a union, so a bad link fails as "not a union" and the
 * reason (not http(s), or a type the browser would run) is nested under that.
 * The nested reason is the one a person can act on.
 */
export function eventDocumentInputMessage(error: z.ZodError): string {
  const custom = firstCustomMessage(error.issues)
  if (custom !== null) return custom
  return error.issues[0]?.message ?? 'Check the document and try again.'
}

function firstCustomMessage(issues: readonly z.ZodIssue[]): string | null {
  for (const issue of issues) {
    if (issue.code === 'custom') return issue.message
    if (issue.code !== 'invalid_union') continue
    // Zod 4 stores each branch's issues on `errors`. A rejected link and a
    // rejected upload are both branches, so the reason a person can act on is
    // one level down.
    for (const nested of issue.errors) {
      const found = firstCustomMessage(nested)
      if (found !== null) return found
    }
  }
  return null
}
